import { Link } from "react-router-dom";
import { Check } from "lucide-react";

// First-run guide — appears at the top of the Dashboard until the user has
// at least one funding source, one bill, and one income row. Each item
// links to the corresponding management page and displays a Check mark
// once satisfied.
function GettingStartedStepItem({ done, num, title, body, ctaLabel, to, testId }) {
  return (
    <li className={`flex items-start gap-3 p-3 rounded-xl ${done ? "bg-[#EEF3EC]" : "bg-white"} border ${done ? "border-[#B8CFB1]" : "border-[#EBE6E0]"}`} data-testid={testId}>
      <div className={`flex-none w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold ${done ? "bg-[#3B5B3E] text-white" : "bg-[#F1EEE7] text-[#59605C]"}`}>
        {done ? <Check size={14} /> : num}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold text-[#2C302E]">{title}</div>
        <p className="text-xs text-[#59605C] mt-0.5 leading-relaxed">{body}</p>
      </div>
      {!done && (
        <Link to={to} className="btn-primary text-xs shrink-0 !py-1 !px-2.5" data-testid={`${testId}-cta`}>
          {ctaLabel}
        </Link>
      )}
    </li>
  );
}

export default function GettingStartedCard({ hasAccounts, hasBills, hasIncome }) {
  const completeCount = [hasAccounts, hasBills, hasIncome].filter(Boolean).length;
  return (
    <div className="card-soft p-6 bg-[#FFFBF0] border border-[#E5D9BA]" data-testid="dashboard-getting-started">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#7A4E1F]">Getting started</div>
          <h2 className="font-heading text-xl font-medium text-[#2C302E]">Set up in three quick steps</h2>
          <p className="text-xs text-[#59605C] mt-1 max-w-xl">Once these three are in place, the Dashboard will forecast your real balance for every pay period and month. Finish any step in the order that makes sense to you.</p>
        </div>
        <div className="text-xs font-semibold text-[#7A4E1F] whitespace-nowrap">
          {completeCount}/3 done
        </div>
      </div>
      <ol className="space-y-2">
        <GettingStartedStepItem
          done={hasAccounts}
          num="1"
          title="Add your spending accounts"
          body="Add every bank account (checking, savings) AND any cash you actually carry. Your real balance is the total of what you can spend today."
          ctaLabel="Add account →"
          to="/app/accounts"
          testId="dashboard-getting-started-accounts"
        />
        <GettingStartedStepItem
          done={hasBills}
          num="2"
          title="Add your bills"
          body="Rent, utilities, subscriptions, credit-card minimums. Mark each as recurring (monthly / weekly) so it rolls forward automatically."
          ctaLabel="Add bill →"
          to="/app/bills"
          testId="dashboard-getting-started-bills"
        />
        <GettingStartedStepItem
          done={hasIncome}
          num="3"
          title="Add your income sources"
          body="Paychecks (weekly / biweekly / monthly) plus one-time money like bonuses, tax refunds, or side-gig payouts. The app projects them forward for you."
          ctaLabel="Add income →"
          to="/app/income"
          testId="dashboard-getting-started-income"
        />
      </ol>
    </div>
  );
}
